import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Eyebrow } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const BUDGETS = [
  { label: "All budgets", value: "" },
  { label: "Under ₹1,000", value: "0-1000" },
  { label: "₹1,000 – ₹2,500", value: "1000-2500" },
  { label: "₹2,500+", value: "2500-999999" },
];

export default function Collections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ brands: [], categories: [], collections: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [budget, setBudget] = useState("");

  const brand = searchParams.get("brand") || "";
  const category = searchParams.get("category") || "";
  const occasion = searchParams.get("occasion") || "";

  useEffect(() => {
    document.title = "Collections — THE GOOD GRID";
    api.get("/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: { brand, category, occasion, q } })
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [brand, category, occasion, q]);

  const filtered = useMemo(() => {
    if (!budget) return products;
    const [lo, hi] = budget.split("-").map(Number);
    return products.filter((p) => {
      if (p.price_from == null && p.price_to == null) return true;
      const from = p.price_from ?? 0;
      const to = p.price_to ?? Number.MAX_SAFE_INTEGER;
      return to >= lo && from <= hi;
    });
  }, [products, budget]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const occasions = taxonomy.collections.map((c) => c.title).filter((t) => t !== "Custom Corporate Gifting");

  const Chip = ({ active, onClick, children, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-[10px] font-mono uppercase tracking-[0.18em] px-4 py-2 border transition-colors duration-300 ${
        active ? "border-champagne bg-champagne text-charcoal" : "border-white/15 text-ivory/60 hover:border-champagne/60 hover:text-ivory"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div data-testid="collections-page" className="bg-charcoal text-ivory min-h-screen pt-28 lg:pt-36 px-4 sm:px-8 lg:px-16 pb-24">
      <Reveal><Eyebrow>The Catalogue</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight">Collections</h1>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="text-ivory/50 mt-4 max-w-xl text-sm leading-relaxed">
          Curated products from trusted brands. Every listing shows Price on Request — corporate pricing is built around your quantity and brief.
        </p>
      </Reveal>

      <div data-testid="collections-filters" className="mt-12 space-y-6 border-y border-white/10 py-8">
        <div className="flex flex-col lg:flex-row gap-5 lg:items-center">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-ivory/35" />
            <input
              data-testid="collections-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products or brands"
              className="w-full bg-transparent border border-white/15 focus:border-champagne text-ivory placeholder:text-ivory/30 text-sm pl-11 pr-4 py-3 outline-none transition-colors"
            />
          </div>
          <div className="flex items-center gap-3">
            <SlidersHorizontal size={14} className="text-ivory/40" />
            <select
              data-testid="collections-budget-filter"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="bg-transparent border border-white/15 focus:border-champagne text-ivory text-sm px-4 py-3 outline-none"
            >
              {BUDGETS.map((b) => <option key={b.value} value={b.value} className="bg-charcoal">{b.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip testid="filter-brand-all" active={!brand} onClick={() => setParam("brand", "")}>All brands</Chip>
          {taxonomy.brands.map((b) => (
            <Chip key={b.name} testid={`filter-brand-${b.name.toLowerCase()}`} active={brand === b.name} onClick={() => setParam("brand", b.name)}>{b.name}</Chip>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip testid="filter-category-all" active={!category} onClick={() => setParam("category", "")}>All categories</Chip>
          {taxonomy.categories.map((c) => (
            <Chip key={c.name} testid={`filter-category-${c.name.toLowerCase().replace(/\s+/g, "-")}`} active={category === c.name} onClick={() => setParam("category", c.name)}>{c.name}</Chip>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip testid="filter-occasion-all" active={!occasion} onClick={() => setParam("occasion", "")}>All occasions</Chip>
          {occasions.map((o) => (
            <Chip key={o} testid={`filter-occasion-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} active={occasion === o} onClick={() => setParam("occasion", o)}>{o}</Chip>
          ))}
        </div>
      </div>

      {loading ? (
        <p data-testid="collections-loading" className="text-ivory/40 font-mono text-xs uppercase tracking-[0.25em] mt-16">Loading catalogue…</p>
      ) : filtered.length === 0 ? (
        <p data-testid="collections-empty" className="text-ivory/40 mt-16 font-serif text-2xl">Nothing matches that brief — try widening the filters.</p>
      ) : (
        <div data-testid="collections-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14 mt-14">
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.06}>
              <ProductCard product={p} index={i} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
